import { LoadState } from '@/ui/LoadState';
/**
 * WhoToFollowPanel — right-rail suggested profiles.
 *
 * Each row is its own FollowRow subcomponent (per Rules of Hooks —
 * useFollow must be called at the top level of a component, not in a loop).
 */
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { Panel, SectionLabel, Avatar, Button, TierBadge, deriveTier } from '@/ui';
import { useSuggestedProfiles, useFollow, useRequireAuth, followButtonLabel } from '@/hooks';
import { formatCount } from '@/components/formatCount';
import { PanelEmpty } from './PanelEmpty';
import type { SuggestedProfile } from '@/core/models';

/* ── Styled helpers ───────────────────────────────────────────────────────── */

const ViewAll = styled(Link)`
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
  text-decoration: underline;
  text-underline-offset: 2px;
  cursor: pointer;
  &:hover {
    color: ${({ theme }) => theme.color.textPrimary};
  }
`;

const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Row = styled.li`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Info = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1px;
`;

const NameLine = styled.span`
  display: flex;
  align-items: center;
  gap: 5px;
  min-width: 0;
`;

const Name = styled.span`
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: 600;
  color: ${({ theme }) => theme.color.textPrimary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
  flex: 0 1 auto;
`;

const Handle = styled.span`
  font-family: ${({ theme }) => theme.font.mono};
  font-variant-numeric: tabular-nums;
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;

const Followers = styled.span`
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;

/* ── Per-row subcomponent (calls useFollow at top level) ─────────────────── */

function FollowRow({ profile }: { profile: SuggestedProfile }) {
  const { isFollowing, isRequested, toggleFollow } = useFollow(profile.handle);
  const requireAuth = useRequireAuth();

  function handleFollow() {
    if (!requireAuth('follow')) return;
    void toggleFollow();
  }

  return (
    <Row>
      <Avatar name={profile.name} size={36} />
      <Info>
        <NameLine>
          <Name>{profile.name}</Name>
          <TierBadge tier={deriveTier(profile.verified)} />
        </NameLine>
        <Handle>{profile.handle}</Handle>
        {profile.followerCount > 0 && (
          <Followers>
            {formatCount(profile.followerCount)}{' '}
            {profile.followerCount === 1 ? 'follower' : 'followers'}
          </Followers>
        )}
      </Info>
      <Button
        variant={isFollowing || isRequested ? 'secondary' : 'outline'}
        onClick={handleFollow}
        aria-label={`${followButtonLabel(isFollowing, isRequested)} ${profile.handle}`}
      >
        {followButtonLabel(isFollowing, isRequested)}
      </Button>
    </Row>
  );
}

/* ── Panel ────────────────────────────────────────────────────────────────── */

export interface WhoToFollowPanelProps {
  title?: string;
}

export function WhoToFollowPanel({ title = 'Who to Follow' }: WhoToFollowPanelProps) {
  const profiles = useSuggestedProfiles();

  return (
    <Panel>
      <SectionLabel
        as="h2"
        action={
          <ViewAll to="/explore" aria-label="View all suggestions">
            View all
          </ViewAll>
        }
      >
        {title}
      </SectionLabel>
      {profiles.loading || profiles.error ? (
        <LoadState
          loading={profiles.loading}
          error={profiles.error}
          retry={profiles.retry}
          label="suggestions"
        />
      ) : profiles.length === 0 ? (
        <PanelEmpty>No suggestions yet.</PanelEmpty>
      ) : (
        <List>
          {profiles.slice(0, 3).map((profile) => (
            <FollowRow key={profile.handle} profile={profile} />
          ))}
        </List>
      )}
    </Panel>
  );
}
